import { fmt } from './utils.js';

// ─── INVOICE / QUOTE SHEET ────────────────────────────────────────────────────
// One printable document used by every "export with pictures" path: the cart
// quote, a customer's past order, and the admin order invoice.
//
// WHY PRINT AND NOT jsPDF/html2canvas
// html2canvas cannot shape Complex Text Layout, so Arabic comes out broken —
// that is why it was dropped here previously. The browser's own print engine
// renders Arabic correctly and produces a PDF via "Save as PDF", so the sheet
// is plain HTML and we let the browser do the typesetting.
//
// WHY THUMBNAILS AND NOT THE ORIGINAL IMAGES
// Product photos are full-resolution (hundreds of KB to several MB each). A
// 30-line order embedding them raw yields a 10-30 MB PDF that is painful to
// email or send on WhatsApp, and it needs the network at print time — slow
// loads print as blank boxes. Each unique image is instead drawn to a canvas
// at THUMB_PX and re-encoded as a JPEG data URI (~3-6 KB), so the document is
// self-contained and a typical order lands in the low hundreds of KB.

const THUMB_PX = 110;      // enough for a crisp ~11mm print cell at 300dpi
const THUMB_QUALITY = 0.62;

const loadImage = (url) => new Promise((resolve, reject) => {
  const img = new Image();
  img.crossOrigin = 'anonymous';   // required or the canvas is tainted
  img.onload  = () => resolve(img);
  img.onerror = reject;
  img.src = url;
});

// Downscale one image to a small JPEG data URI. Returns null when the image
// can't be read (404, offline) and the ORIGINAL url when the canvas is tainted
// by CORS — a bigger file still beats a missing picture.
async function toThumbnail(url) {
  try {
    const img = await loadImage(url);
    const scale = Math.min(1, THUMB_PX / Math.max(img.naturalWidth, img.naturalHeight));
    const w = Math.max(1, Math.round(img.naturalWidth  * scale));
    const h = Math.max(1, Math.round(img.naturalHeight * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ECEAE4';           // flatten alpha; JPEG has no transparency
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0, w, h);
    try {
      return canvas.toDataURL('image/jpeg', THUMB_QUALITY);
    } catch {
      return url;                        // tainted canvas → fall back to remote
    }
  } catch {
    return null;                         // unreachable image → render no cell
  }
}

// Compress a list of image urls once each, in parallel. Returns Map(url → src).
export async function buildThumbMap(urls) {
  const unique = [...new Set((urls || []).filter(Boolean))];
  const entries = await Promise.all(
    unique.map(async (u) => [u, await toThumbnail(u)])
  );
  return new Map(entries.filter(([, v]) => v));
}

const esc = (s) => String(s ?? '').replace(/[&<>"]/g, c => (
  { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]
));

/**
 * Build the printable sheet.
 *
 * @param items      [{ sku, name_en, name_ar, qty, price, image, variant_label }]
 * @param priceOf    (item) => unit price actually charged/quoted
 * @param thumbs     Map(url → data URI) from buildThumbMap, or null for no pictures
 * @param kind       'quote' | 'order' | 'invoice' — drives the heading + footer
 * @param party      { company_name, contact_name, phone } shown top-right
 */
export function invoiceSheetHtml({
  lang = 'ar', kind = 'quote', refId, dateMs, party, items = [],
  total, showPrice = true, showVat = false, thumbs = null, notes,
}) {
  const isAr = lang === 'ar';
  const dir  = isAr ? 'rtl' : 'ltr';
  const dateStr = new Date(dateMs || Date.now())
    .toLocaleDateString(isAr ? 'ar-EG' : 'en-GB', { year: 'numeric', month: 'long', day: 'numeric' });

  const HEADING = {
    quote:   { ar: 'عرض سعر',  en: 'PRICE QUOTE' },
    order:   { ar: 'طلب',      en: 'ORDER' },
    invoice: { ar: 'فاتورة',   en: 'INVOICE' },
  }[kind] || { ar: 'عرض سعر', en: 'PRICE QUOTE' };

  const FOOTNOTE = kind === 'quote'
    ? (isAr ? 'هذا عرض سعر وليس طلباً مؤكداً.' : 'This is a price quotation, not a confirmed order.')
    : (isAr ? 'سجل طلب مؤكد.' : 'Record of a confirmed order.');

  const withPics = !!thumbs;

  const itemRows = items.map((item, i) => {
    const src = withPics && item.image ? thumbs.get(item.image) : null;
    const unit = typeof item.price === 'number' || item.price != null ? Number(item.price) : null;
    const line = unit != null ? unit * item.qty : null;
    return `
      <tr>
        <td>${i + 1}</td>
        ${withPics ? `<td class="pic">${src
          ? `<img src="${src}" alt=""/>`
          : '<span class="nopic"></span>'}</td>` : ''}
        <td class="sku">${esc(item.sku)}</td>
        <td>
          <span style="font-weight:600">${esc(item.name_en || '')}</span>
          ${item.name_ar ? `<span class="ar">${esc(item.name_ar)}</span>` : ''}
          ${item.variant_label ? `<span class="variant">${esc(item.variant_label)}</span>` : ''}
        </td>
        <td style="text-align:center;font-weight:700">${esc(item.qty)}</td>
        ${showPrice ? `<td style="text-align:right">${unit != null ? fmt(unit) : '—'}</td>` : ''}
        ${showPrice ? `<td style="text-align:right;font-weight:700">${line != null ? fmt(line) : '—'}</td>` : ''}
      </tr>`;
  }).join('');

  return `<!DOCTYPE html>
<html dir="${dir}" lang="${isAr ? 'ar' : 'en'}">
<head>
  <meta charset="UTF-8"/>
  <title>${isAr ? HEADING.ar : HEADING.en} — ${esc(refId)}</title>
  <link rel="preconnect" href="https://fonts.googleapis.com"/>
  <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;600;700;800;900&family=Tajawal:wght@400;500;600;700;800&display=swap" rel="stylesheet"/>
  <style>
    *{box-sizing:border-box;margin:0;padding:0}
    body{font-family:'DM Sans','Tajawal',Arial,sans-serif;color:#1A1F1B;padding:32px 40px;max-width:820px;margin:0 auto;font-size:13px}
    .header{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid #002A17;padding-bottom:16px;margin-bottom:24px}
    .brand h1{font-size:22px;font-weight:900;color:#002A17}
    .brand p{font-size:11px;color:#6F7260;margin-top:3px}
    .meta{text-align:${isAr ? 'left' : 'right'}}
    .meta h2{font-size:16px;font-weight:800;color:#0E3D28}
    .meta p{font-size:11px;color:#aaa;margin-top:4px}
    .retailer{margin-top:8px;font-size:12px}
    .retailer strong{display:block;font-weight:700;color:#002A17}
    table{width:100%;border-collapse:collapse;margin-bottom:20px}
    thead tr{background:#F7F4EF}
    th{padding:9px 12px;text-align:${isAr ? 'right' : 'left'};font-size:11px;font-weight:700;color:#0E3D28;border-bottom:1px solid #e8e3da}
    td{padding:8px 12px;border-bottom:1px solid #f0ede8;vertical-align:middle}
    td.sku{font-family:monospace;font-size:11px;color:#6F7260}
    td.pic{width:56px}
    td.pic img{width:44px;height:44px;object-fit:contain;border-radius:6px;background:#ECEAE4;display:block}
    .nopic{display:block;width:44px;height:44px;border-radius:6px;background:#F2F0EC}
    .ar{display:block;direction:rtl;font-family:'Tajawal',sans-serif;font-size:13px;color:#555}
    .variant{font-size:10px;background:#F0F1EC;color:#6F7260;border-radius:3px;padding:1px 5px}
    .total-box{display:flex;justify-content:flex-end;margin-bottom:28px}
    .total-inner{background:#F7F4EF;border-radius:8px;padding:14px 20px;min-width:240px;text-align:${isAr ? 'left' : 'right'}}
    .total-inner .label{font-size:11px;color:#888;margin-bottom:4px}
    .total-inner .amount{font-size:17px;font-weight:900;color:#002A17}
    .note{font-size:12px;color:#555;margin-bottom:18px}
    .footer{border-top:1px solid #e8e3da;padding-top:12px;display:flex;justify-content:space-between;font-size:10px;color:#aaa}
    @media print{
      body{padding:18px 24px}
      @page{margin:12mm}
      tr{break-inside:avoid}
    }
  </style>
</head>
<body>
  <div class="header">
    <div class="brand">
      <h1>Sedra Home</h1>
      <p style="font-family:'Tajawal',sans-serif">سدرة هوم — Wholesale</p>
      <p>wholesale.sedrahome.com</p>
    </div>
    <div class="meta">
      <h2>${isAr ? HEADING.ar : HEADING.en}</h2>
      <p>${esc(refId)}</p>
      <p>${dateStr}</p>
      ${party ? `<div class="retailer">
        <strong>${esc(party.company_name || '')}</strong>
        <span>${esc(party.contact_name || '')}</span>
        ${party.phone ? `<span> · ${esc(party.phone)}</span>` : ''}
      </div>` : ''}
    </div>
  </div>

  <table>
    <thead>
      <tr>
        <th>#</th>
        ${withPics ? `<th>${isAr ? 'صورة' : 'Image'}</th>` : ''}
        <th>SKU</th>
        <th>${isAr ? 'المنتج / Product' : 'Product'}</th>
        <th style="text-align:center">${isAr ? 'الكمية' : 'Qty'}</th>
        ${showPrice ? `<th style="text-align:right">${isAr ? 'سعر الوحدة' : 'Unit Price'}</th>` : ''}
        ${showPrice ? `<th style="text-align:right">${isAr ? 'المجموع' : 'Subtotal'}</th>` : ''}
      </tr>
    </thead>
    <tbody>${itemRows}</tbody>
  </table>

  ${showPrice && total != null ? `<div class="total-box">
    <div class="total-inner">
      ${showVat ? `<div class="label">${isAr ? 'شامل ضريبة القيمة المضافة 14٪' : 'Includes 14% VAT'}</div>` : ''}
      <div class="amount">${isAr ? 'الإجمالي' : 'TOTAL'}: ${fmt(total)}</div>
    </div>
  </div>` : ''}

  ${notes ? `<div class="note">${esc(notes)}</div>` : ''}

  <div class="footer">
    <span>${FOOTNOTE}</span>
    <span>${new Date().toISOString().slice(0, 10)}</span>
  </div>

  <script>
    // Give the webfonts a moment so Arabic shapes correctly in the PDF.
    setTimeout(() => { window.print(); }, 800);
  </script>
</body>
</html>`;
}

// Open the sheet in a new window. Returns false when popups are blocked.
export function openSheet(html) {
  const w = window.open('', '_blank', 'width=900,height=700');
  if (!w) return false;
  w.document.write(html);
  w.document.close();
  return true;
}

/**
 * Compress pictures (when asked) and open the sheet. Single entry point for
 * every export-with-pictures button in the app.
 */
export async function openInvoiceSheet({ withPictures = false, items = [], ...rest }) {
  const thumbs = withPictures
    ? await buildThumbMap(items.map(i => i.image))
    : null;
  return openSheet(invoiceSheetHtml({ ...rest, items, thumbs }));
}
